import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { ManagementAttentionResponse } from "@/api/intelligence";
import { formatInr, formatPercentChange } from "@/lib/intelligenceFormat";
import { IntelligenceSeverityBadge } from "./IntelligenceSeverityBadge";
import { AlertTriangle } from "lucide-react";

type Props = {
	data: ManagementAttentionResponse;
	basePath?: string;
	compact?: boolean;
};

function formatValue(metricKey: string, value: number | null): string {
	if (value === null) return "—";
	if (
		metricKey.includes("revenue") ||
		metricKey === "collections" ||
		metricKey === "outstanding" ||
		metricKey === "refunds" ||
		metricKey === "discounts" ||
		metricKey.includes("expense") ||
		metricKey.includes("cash_flow") ||
		metricKey.includes("stock") ||
		metricKey === "receivables_overdue"
	) {
		return formatInr(value);
	}
	if (metricKey.includes("pct") || metricKey.includes("share") || metricKey.includes("efficiency")) {
		return `${value}%`;
	}
	return value.toLocaleString("en-IN");
}

export function ManagementAttentionList({
	data,
	basePath = "/hospital-admin",
	compact = false,
}: Props) {
	const items = compact ? data.items.slice(0, 6) : data.items;

	return (
		<div className="space-y-3">
			{!compact && (
				<div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
					<span>
						<span className="font-semibold text-rose-700">{data.summary.critical}</span> high
					</span>
					<span>
						<span className="font-semibold text-amber-700">{data.summary.attention}</span> medium
					</span>
					<span>
						<span className="font-semibold text-slate-600">{data.summary.observation}</span> info
					</span>
					<span>
						<span className="font-semibold text-emerald-700">{data.summary.opportunity}</span> positive
					</span>
				</div>
			)}

			{items.length === 0 ? (
				<p className="text-sm text-muted-foreground py-4">
					No rule thresholds triggered for this period.
				</p>
			) : (
				<ul className="space-y-3">
					{items.map((item) => (
						<li
							key={item.id}
							className="rounded-lg border bg-white px-4 py-3 shadow-sm space-y-2"
						>
							<div className="flex items-start justify-between gap-3">
								<div className="min-w-0">
									<div className="flex items-center gap-2 flex-wrap">
										<IntelligenceSeverityBadge severity={item.severity} />
										<span className="font-medium text-slate-900">{item.title}</span>
									</div>
									<p className="text-sm text-muted-foreground mt-1">{item.description}</p>
								</div>
							</div>
							<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
								{item.currentValue !== null && (
									<span>
										Current:{" "}
										<strong className="text-foreground tabular-nums">
											{formatValue(item.metricKey, item.currentValue)}
										</strong>
									</span>
								)}
								{item.previousValue !== null && (
									<span>
										Previous:{" "}
										<strong className="text-foreground tabular-nums">
											{formatValue(item.metricKey, item.previousValue)}
										</strong>
									</span>
								)}
								{item.changePercent !== null && (
									<span>
										Variance:{" "}
										<strong className="tabular-nums">
											{formatPercentChange(item.changePercent)}
										</strong>
									</span>
								)}
								<span>Threshold: {item.thresholdLabel}</span>
							</div>
							{item.recommendedAction && (
								<p className="text-xs text-slate-600">{item.recommendedAction}</p>
							)}
							{item.drillDownPath && (
								<Link
									to={item.drillDownPath.replace("/hospital-admin", basePath)}
									className="text-xs font-medium text-indigo-600 hover:underline"
								>
									View detail →
								</Link>
							)}
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

export function ManagementAttentionSection({
	data,
	basePath = "/hospital-admin",
}: {
	data: { managementAttention: ManagementAttentionResponse };
	basePath?: string;
}) {
	const attention = data.managementAttention;

	return (
		<section className="space-y-4">
			<div className="flex items-start justify-between gap-4">
				<div>
					<h2 className="text-lg font-semibold text-slate-900">Management Attention</h2>
					<p className="text-sm text-muted-foreground">{attention.message}</p>
				</div>
				{attention.items.length > 6 && (
					<Link
						to={`${basePath}/intelligence/attention`}
						className="text-sm font-medium text-indigo-600 hover:underline shrink-0"
					>
						View all ({attention.summary.total})
					</Link>
				)}
			</div>

			<Card className="border-indigo-200/60">
				<CardHeader className="pb-2">
					<CardTitle className="text-base font-medium flex items-center gap-2">
						<AlertTriangle className="h-4 w-4 text-amber-600" />
						Rule-based alerts
					</CardTitle>
					<p className="text-xs text-muted-foreground">
						Deterministic thresholds — no AI. {attention.limitations[0]}
					</p>
				</CardHeader>
				<CardContent>
					<ManagementAttentionList data={attention} basePath={basePath} compact />
				</CardContent>
			</Card>
		</section>
	);
}
